import { hasAccessRoles } from "../utils/userHelper";
import { groupNames, type Queue, type QueueDefinition } from "./types";

const pollingInterval = 60000;

// Date 14 days ago, used for rejected queue
const today = new Date();
const date14DaysAgo = new Date(
  today.setDate(today.getDate() - 14)
).toISOString();

const allowedRoles = [
  "admin-no",
  "caseworker-no",
  "reader-no",
  "admin-all",
  "reader-all"
];

// @overWriteAccessRoles: Array of roles for specific queues
// Check if user has access to menu
function hasAccessToMenu(
  userRoles: string[],
  overwriteAccessRoles?: string[]
): boolean {
  return hasAccessRoles(userRoles, overwriteAccessRoles ?? allowedRoles);
}

// queue for active applications
function queueActiveLoanProcess(userRoles: string[]): Queue | null {
  const name = "no-active-cases-queue";
  if (hasAccessToMenu(userRoles)) {
    return {
      label: "Active",
      name: name,
      queueFilter:
        "?flowDefinitionId=common-process&data.country=NO&view=queue-items&status=active&hasErrors=false",
      queueSort: "sort=updatedAt&dir=-1",
      limit: 20,
      pollingInterval: pollingInterval,
      groupName: groupNames.All_APPLICATIONS
    };
  }
  return null;
}

function queueReadyForDisbursementProcess(userRoles: string[]): Queue | null {
  const name = "no-ready-for-disbursement-cases-queue";
  if (hasAccessToMenu(userRoles)) {
    return {
      label: "Ready for disbursement",
      name: name,
      queueFilter:
        "?flowDefinitionId=common-process&data.country=NO&view=queue-items&status=active&data.queues.ready-for-disbursement=true",
      queueSort: "sort=createdAt&dir=-1",
      limit: 20,
      pollingInterval: pollingInterval,
      groupName: groupNames.All_APPLICATIONS
    };
  }
  return null;
}

function queueDisbursedProcess(userRoles: string[]): Queue | null {
  const name = "no-disbursed-cases-queue";
  if (hasAccessToMenu(userRoles)) {
    return {
      label: "Disbursed",
      name: name,
      queueFilter:
        "?flowDefinitionId=common-process&data.country=NO&view=queue-items&data.queues.disbursed=true",
      queueSort: "sort=updatedAt&dir=-1",
      limit: 20,
      pollingInterval: pollingInterval,
      groupName: groupNames.All_APPLICATIONS
    };
  }
  return null;
}

function queueWorkingListProcess(userRoles: string[]): Queue | null {
  const name = "no-working-list-queue";
  if (hasAccessToMenu(userRoles)) {
    const currentTimestamp = new Date().toISOString();
    return {
      label: "Direct channel - not signed",
      name: name,
      // Direct channel applications that are approved but not signed
      // Excludes cases where no-reply timeout hasn't expired yet
      queueFilter: `?flowDefinitionId=common-process&data.country=NO&view=queue-items&status=active&data.queues.working-list=true&data.caseWork.no-reply.0.validAtWorkingListFrom=<${currentTimestamp}`,
      queueSort: "sort=createdAt&dir=-1",
      limit: 20,
      pollingInterval: pollingInterval,
      groupName: groupNames.CASEWORKER_TASKS
    };
  }
  return null;
}

function queueRejectedProcess(userRoles: string[]): Queue | null {
  const name = "no-declined-cases-queue";
  if (hasAccessToMenu(userRoles)) {
    return {
      label: "Rejected (Last 14 days)",
      name: name,
      queueFilter: `?flowDefinitionId=common-process&data.country=NO&view=queue-items&status[]=completed&status[]=archived&data.queues.disbursed=false&updatedAt=>${date14DaysAgo}`,
      queueSort: "sort=updatedAt&dir=-1",
      limit: 20,
      pollingInterval: pollingInterval,
      groupName: groupNames.All_APPLICATIONS
    };
  }
  return null;
}

// queue case worker tasks

function queueManualReviewProcess(userRoles: string[]): Queue | null {
  const name = "no-manual-casework-review";
  if (hasAccessToMenu(userRoles)) {
    return {
      label: "Income Review",
      name: name,
      queueFilter:
        "?flowDefinitionId=common-process&data.country=NO&view=queue-items&status=active&data.queues.manual-payout-approval=true",
      queueSort: "sort=createdAt&dir=-1",
      pollingEnabled: false,
      limit: 20,
      pollingInterval: pollingInterval,
      groupName: groupNames.CASEWORKER_TASKS
    };
  }
  return null;
}

function queueManualAMLReviewProcess(userRoles: string[]): Queue | null {
  const name = "no-manual-casework-aml";
  if (hasAccessToMenu(userRoles)) {
    return {
      label: "AML Review",
      name: name,
      queueFilter:
        "?flowDefinitionId=common-process&data.country=NO&view=queue-items&status=active&data.queues.manual-pep-review=true",
      queueSort: "sort=createdAt&dir=-1",
      pollingEnabled: false,
      limit: 20,
      pollingInterval: pollingInterval,
      groupName: groupNames.CASEWORKER_TASKS
    };
  }
  return null;
}

function queueManualKarCheckProcess(userRoles: string[]): Queue | null {
  const name = "no-manual-kar-check";
  if (hasAccessToMenu(userRoles)) {
    return {
      label: "KAR Check",
      name: name,
      queueFilter:
        "?flowDefinitionId=common-process&data.country=NO&view=queue-items&status=active&data.queues.manual-kar-check=true",
      queueSort: "sort=createdAt&dir=-1",
      pollingEnabled: false,
      limit: 20,
      pollingInterval: pollingInterval,
      groupName: groupNames.CASEWORKER_TASKS
    };
  }
  return null;
}

function queueCrossDataCheckProcess(userRoles: string[]): Queue | null {
  const name = "no-cross-data-check";
  if (hasAccessToMenu(userRoles)) {
    return {
      label: "Cross Data Check",
      name: name,
      queueFilter:
        "?flowDefinitionId=common-process&data.country=NO&view=queue-items&data.queues.cross-data-check=true",
      queueSort: "sort=createdAt&dir=-1",
      pollingEnabled: false,
      limit: 20,
      pollingInterval: pollingInterval,
      groupName: groupNames.CASEWORKER_TASKS
    };
  }
  return null;
}

function queueManualCheckFinalProcess(userRoles: string[]): Queue | null {
  const name = "no-manual-check-final";
  if (hasAccessToMenu(userRoles)) {
    return {
      label: "Disbursement Check",
      name: name,
      queueFilter:
        "?flowDefinitionId=common-process&data.country=NO&view=queue-items&data.queues.manual-check-final=true",
      queueSort: "sort=createdAt&dir=-1",
      pollingEnabled: false,
      limit: 20,
      pollingInterval: pollingInterval,
      groupName: groupNames.CASEWORKER_TASKS
    };
  }
  return null;
}

function queueManualCheckFinalHighPriorityProcess(
  userRoles: string[]
): Queue | null {
  const name = "no-manual-check-final-high-priority";
  if (hasAccessToMenu(userRoles)) {
    const currentTimestamp = new Date().toISOString();
    return {
      label: "Disbursement check - high priority",
      name: name,
      queueFilter: `?flowDefinitionId=common-process&data.country=NO&view=queue-items&status=active&data.queues.manual-check-final-high-priority=true&data.caseWork.no-reply.0.validAtWorkingListFrom=<${currentTimestamp}`,
      queueSort: "sort=createdAt&dir=-1",
      limit: 20,
      pollingInterval: pollingInterval,
      groupName: groupNames.CASEWORKER_TASKS
    };
  }
  return null;
}

function queueOnFollowUpCasesProcess(userRoles: string[]): Queue | null {
  const name = "no-on-follow-up-cases-queue";
  if (hasAccessToMenu(userRoles)) {
    return {
      label: "On Follow Up",
      name: name,
      queueFilter:
        "?flowDefinitionId=common-process&data.country=NO&view=queue-items&data.queues.onFollowUp=true",
      queueSort: "sort=createdAt&dir=-1",
      pollingEnabled: true,
      limit: 20,
      pollingInterval: pollingInterval,
      groupName: groupNames.CASEWORKER_TASKS
    };
  }
  return null;
}

function queueOnHoldCasesProcess(userRoles: string[]): Queue | null {
  const name = "no-on-hold-cases-queue";
  if (hasAccessToMenu(userRoles)) {
    return {
      label: "On Hold",
      name: name,
      queueFilter:
        "?flowDefinitionId=common-process&data.country=NO&view=queue-items&data.queues.onHold=true",
      queueSort: "sort=createdAt&dir=-1",
      pollingEnabled: true,
      limit: 20,
      pollingInterval: pollingInterval,
      groupName: groupNames.CASEWORKER_TASKS
    };
  }
  return null;
}

// queue for customer tasks

function queueAcceptLoanProcess(userRoles: string[]): Queue | null {
  const name = "no-awaiting-accept-cases-queue";
  if (hasAccessToMenu(userRoles)) {
    return {
      label: "Accept Loan Offer",
      name: name,
      queueFilter:
        "?flowDefinitionId=common-process&data.country=NO&view=queue-items&data.queues.awaiting-accept=true",
      queueSort: "sort=updatedAt&dir=-1",
      limit: 20,
      pollingInterval: pollingInterval,
      groupName: groupNames.CUSTOMER_TASKS
    };
  }
  return null;
}

function queueStartInformationProcess(userRoles: string[]): Queue | null {
  const name = "no-start-information";
  if (hasAccessToMenu(userRoles)) {
    return {
      label: "Start Information",
      name: name,
      queueFilter:
        "?flowDefinitionId=common-process&data.country=NO&view=queue-items&status=active&data.queues.start-information=true",
      queueSort: "sort=updatedAt&dir=-1",
      limit: 20,
      pollingInterval: pollingInterval,
      groupName: groupNames.CUSTOMER_TASKS
    };
  }
  return null;
}

function queueCustomerInfoProcess(userRoles: string[]): Queue | null {
  const name = "no-customer-info";
  if (hasAccessToMenu(userRoles)) {
    return {
      label: "Customer Info",
      name: name,
      queueFilter:
        "?flowDefinitionId=common-process&data.country=NO&view=queue-items&status=active&data.queues.customer-info=true",
      queueSort: "sort=updatedAt&dir=-1",
      limit: 20,
      pollingInterval: pollingInterval,
      groupName: groupNames.CUSTOMER_TASKS
    };
  }
  return null;
}

function queueRefinanceProcess(userRoles: string[]): Queue | null {
  const name = "no-refinance";
  if (hasAccessToMenu(userRoles)) {
    return {
      label: "Refinance",
      name: name,
      queueFilter:
        "?flowDefinitionId=common-process&data.country=NO&view=queue-items&status=active&data.queues.refinance=true",
      queueSort: "sort=updatedAt&dir=-1",
      limit: 20,
      pollingInterval: pollingInterval,
      groupName: groupNames.CUSTOMER_TASKS
    };
  }
  return null;
}

function queueSblConsentProcess(userRoles: string[]): Queue | null {
  const name = "no-sbl-consent";
  if (hasAccessToMenu(userRoles)) {
    return {
      label: "SBL Consent",
      name: name,
      queueFilter:
        "?flowDefinitionId=common-process&data.country=NO&view=queue-items&status=active&data.queues.sbl-consent=true",
      queueSort: "sort=updatedAt&dir=-1",
      limit: 20,
      pollingInterval: pollingInterval,
      groupName: groupNames.CUSTOMER_TASKS
    };
  }
  return null;
}

function queueInsuranceSaleProcess(userRoles: string[]): Queue | null {
  const name = "no-insurance-sale";
  if (hasAccessToMenu(userRoles)) {
    return {
      label: "Insurance Sale",
      name: name,
      queueFilter:
        "?flowDefinitionId=common-process&data.country=NO&view=queue-items&status=active&data.queues.insurance-sale=true",
      queueSort: "sort=updatedAt&dir=-1",
      limit: 20,
      pollingInterval: pollingInterval,
      groupName: groupNames.CUSTOMER_TASKS
    };
  }
  return null;
}

function queueSignDocumentsProcess(userRoles: string[]): Queue | null {
  const name = "no-awaiting-signing-cases-queue";
  if (hasAccessToMenu(userRoles)) {
    return {
      label: "Sign Documents",
      name: name,
      queueFilter:
        "?flowDefinitionId=common-process&data.country=NO&view=queue-items&status=active&data.queues.awaiting-signing=true",
      queueSort: "sort=updatedAt&dir=-1",
      limit: 20,
      pollingInterval: pollingInterval,
      groupName: groupNames.CUSTOMER_TASKS
    };
  }
  return null;
}

// queue for incidents
function queueFailedCasesUpdateAgentProcess(userRoles: string[]): Queue | null {
  const name = "no-failed-cases-update-agent-queue";
  if (hasAccessToMenu(userRoles)) {
    return {
      label: "Incidents (Update Agent)",
      name: name,
      queueFilter:
        "?flowDefinitionId=common-process&data.country=NO&view=queue-items&status=active&hasErrors=true&data.errors.update-agent.stamp=>0",
      queueSort: "sort=updatedAt&dir=-1",
      pollingEnabled: false,
      limit: 20,
      pollingInterval: pollingInterval,
      groupName: groupNames.INCIDENTS
    };
  }
  return null;
}

function queueFailedCasesProcess(userRoles: string[]): Queue | null {
  const name = "no-failed-cases-queue";
  if (hasAccessToMenu(userRoles)) {
    return {
      label: "Incidents",
      name: name,
      queueFilter:
        "?flowDefinitionId=common-process&data.country=NO&view=queue-items&status=active&hasErrors=true&data.errors.update-agent=!",
      queueSort: "sort=updatedAt&dir=-1",
      pollingEnabled: false,
      limit: 20,
      pollingInterval: pollingInterval,
      groupName: groupNames.INCIDENTS
    };
  }
  return null;
}

function queues(userRoles: string[]): (Queue | null)[] {
  return [
    queueActiveLoanProcess(userRoles),
    queueReadyForDisbursementProcess(userRoles),
    queueDisbursedProcess(userRoles),
    queueWorkingListProcess(userRoles),
    queueRejectedProcess(userRoles),
    queueManualReviewProcess(userRoles),
    queueManualAMLReviewProcess(userRoles),
    queueManualKarCheckProcess(userRoles),
    queueCrossDataCheckProcess(userRoles),
    queueManualCheckFinalProcess(userRoles),
    queueManualCheckFinalHighPriorityProcess(userRoles),
    queueOnFollowUpCasesProcess(userRoles),
    queueOnHoldCasesProcess(userRoles),
    queueAcceptLoanProcess(userRoles),
    queueStartInformationProcess(userRoles),
    queueSblConsentProcess(userRoles),
    queueCustomerInfoProcess(userRoles),
    queueRefinanceProcess(userRoles),
    queueInsuranceSaleProcess(userRoles),
    queueSignDocumentsProcess(userRoles),
    queueFailedCasesUpdateAgentProcess(userRoles),
    queueFailedCasesProcess(userRoles)
  ];
}

export function createQueueDefinitionsNorway(userRoles: string[]): {
  [key: string]: Queue;
} {
  const validQueues = queues(userRoles).filter(
    (queue): queue is Queue => queue !== null
  );
  const queueDefinitions = validQueues.reduce((acc: QueueDefinition, queue) => {
    acc[queue.name] = queue;
    return acc;
  }, {});
  return queueDefinitions;
}

export function createMenuDefinitionsNorway(userRoles: string[]): Queue[] {
  const validQueues = queues(userRoles).filter(
    (queue): queue is Queue => queue !== null
  );
  return validQueues;
}
